// src/components/financials/FinancialDataManager.js
// Updated: Monday, July 27, 2026 at 7:46 PM EDT

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Backup,
  CloudUpload,
  Download,
  FactCheck,
  Restore,
  WarningAmber,
  ManageSearch,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import {
  downloadHistoricalExpenseTemplate,
  downloadHistoricalIncomeTemplate,
  exportCompleteFinancialWorkbook,
  importFinancialRecords,
  readAndValidateFinancialWorkbook,
} from '../../services/financialExcelService';
import {
  downloadFinancialIntegrityReport,
  runFinancialIntegrityScan,
} from '../../services/financialIntegrityService';

const IMPORT_MODES = [
  {
    value: 'restore_exact',
    label: 'Exact backup restore',
    description: 'Restores the exported document IDs and replaces matching financial records.',
  },
  {
    value: 'merge_missing',
    label: 'Restore missing records only',
    description: 'Creates records that are missing and skips records already in Firestore.',
  },
  {
    value: 'replace_matching',
    label: 'Replace matching records',
    description: 'Creates missing records and replaces matching records from the workbook.',
  },
  {
    value: 'historical_income',
    label: 'Mass upload historical income',
    description: 'Imports past contributions from the Historical_Income template.',
  },
  {
    value: 'historical_expenses',
    label: 'Mass upload historical expenses',
    description: 'Imports past expenses from the Historical_Expenses template.',
  },
];

const FinancialDataManager = () => {
  const { currentUser, userProfile, churchProfile } = useAuth();
  const churchId = userProfile?.churchId;
  const isSuperAdmin = userProfile?.role === 'Super Administrator';

  const [isExporting, setIsExporting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importMode, setImportMode] = useState('historical_income');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const selectedMode = useMemo(
    () => IMPORT_MODES.find((mode) => mode.value === importMode),
    [importMode],
  );


  const handleIntegrityScan = async () => {
    setErrorMessage('');
    setIsScanning(true);
    try {
      const result = await runFinancialIntegrityScan(churchId);
      setScanResult(result);
    } catch (error) {
      console.error('Financial integrity scan failed:', error);
      setErrorMessage(error.message || 'The financial integrity scan could not be completed.');
    } finally {
      setIsScanning(false);
    }
  };

  const handleDownloadIntegrityReport = () => {
    try {
      downloadFinancialIntegrityReport(
        scanResult,
        churchProfile?.name || churchProfile?.churchName,
      );
    } catch (error) {
      setErrorMessage(error.message || 'The integrity report could not be downloaded.');
    }
  };

  const handleExport = async () => {
    setErrorMessage('');
    setImportResult(null);
    setIsExporting(true);
    try {
      await exportCompleteFinancialWorkbook({
        churchId,
        churchName: churchProfile?.name || churchProfile?.churchName,
        user: currentUser,
      });
    } catch (error) {
      console.error('Financial export failed:', error);
      setErrorMessage(error.message || 'The financial export could not be created.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelection = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setValidationResult(null);
    setImportResult(null);
    setErrorMessage('');
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setErrorMessage('Select an Excel workbook first.');
      return;
    }

    setIsValidating(true);
    setErrorMessage('');
    setImportResult(null);
    try {
      const result = await readAndValidateFinancialWorkbook(selectedFile, importMode, churchId);
      setValidationResult(result);
    } catch (error) {
      console.error('Workbook validation failed:', error);
      setErrorMessage(error.message || 'The workbook could not be validated.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!validationResult || validationResult.errors.length > 0) {
      setErrorMessage('Validate the workbook and correct all errors before importing.');
      return;
    }

    const dangerousMode = ['restore_exact', 'replace_matching'].includes(importMode);
    const confirmationMessage = dangerousMode
      ? `This operation can replace existing financial records for ${churchProfile?.name || churchId}. Type RESTORE to continue.`
      : `Import ${validationResult.records.length} financial records? Type IMPORT to continue.`;
    const expectedText = dangerousMode ? 'RESTORE' : 'IMPORT';
    const response = window.prompt(confirmationMessage);
    if (response !== expectedText) return;

    setIsImporting(true);
    setErrorMessage('');
    try {
      const result = await importFinancialRecords({
        churchId,
        importMode,
        records: validationResult.records,
        fileName: selectedFile.name,
      });
      setImportResult(result);
    } catch (error) {
      console.error('Financial import failed:', error);
      setErrorMessage(error.message || 'The import did not complete.');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Alert severity="warning">
        Complete financial backup restoration and historical mass imports are restricted to Super Administrators.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Firestore remains your disaster-recovery backup. This Excel feature provides a readable financial archive,
        exact or selective restoration, and mass historical income and expense uploads.
      </Alert>


      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ManageSearch color="primary" />
              <Typography variant="h6" fontWeight="bold">Read-Only Financial Integrity Scan</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Compares each fund's stored balance and currentBalance against opening balances, posted income,
              approved expenses, transfers, and adjustments. This scan does not change any Firestore records.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                startIcon={isScanning ? <CircularProgress size={18} color="inherit" /> : <FactCheck />}
                onClick={handleIntegrityScan}
                disabled={isScanning || !churchId}
              >
                {isScanning ? 'Scanning Financial Records...' : 'Run Read-Only Integrity Scan'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadIntegrityReport}
                disabled={!scanResult}
              >
                Download Scan Report
              </Button>
            </Stack>

            {scanResult && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`${scanResult.summary.fundsWithDifferences} funds with differences`}
                      color={scanResult.summary.fundsWithDifferences ? 'error' : 'success'}
                    />
                    <Chip
                      label={`${scanResult.summary.criticalFindings} critical findings`}
                      color={scanResult.summary.criticalFindings ? 'error' : 'success'}
                    />
                    <Chip
                      label={`${scanResult.summary.warningFindings} warnings`}
                      color={scanResult.summary.warningFindings ? 'warning' : 'default'}
                    />
                  </Stack>

                  <Alert severity={scanResult.summary.criticalFindings ? 'warning' : 'success'}>
                    Expected total: {scanResult.summary.totalExpectedBalance.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}; stored balance total: {scanResult.summary.totalStoredBalance.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}; stored currentBalance total: {scanResult.summary.totalStoredCurrentBalance.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}.
                  </Alert>

                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fund</TableCell>
                          <TableCell align="right">Expected</TableCell>
                          <TableCell align="right">balance</TableCell>
                          <TableCell align="right">currentBalance</TableCell>
                          <TableCell align="right">Difference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scanResult.funds.map((fund) => (
                          <TableRow key={fund.fundId}>
                            <TableCell>{fund.fundName}</TableCell>
                            <TableCell align="right">
                              {fund.expectedBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </TableCell>
                            <TableCell align="right">
                              {fund.storedBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </TableCell>
                            <TableCell align="right">
                              {fund.storedCurrentBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                size="small"
                                label={fund.differenceFromBalance.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                })}
                                color={fund.hasDifference ? 'error' : 'success'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {(scanResult.findings || []).slice(0, 10).map((finding, index) => (
                    <Alert
                      key={`${finding.code}-${finding.documentId || index}`}
                      severity={finding.severity === 'critical' ? 'error' : 'warning'}
                    >
                      <strong>{finding.code}:</strong> {finding.message}
                    </Alert>
                  ))}
                  {scanResult.findings?.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      Showing the first 10 findings. Download the Excel scan report for all findings.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Backup color="primary" />
              <Typography variant="h6" fontWeight="bold">Complete Financial Excel Backup</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Exports funds, batches, every contribution and expense status, reconciliation data, audit records,
              configuration records, and finance-related user data. Each row also includes the complete original
              Firestore document in a rawJson column for restoration.
            </Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <Download />}
                onClick={handleExport}
                disabled={isExporting || !churchId}
              >
                {isExporting ? 'Creating Workbook...' : 'Download Complete Financial Backup'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">Historical Import Templates</Typography>
            <Typography variant="body2" color="text.secondary">
              Use these templates for past income and expense records that are not already in this app.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="outlined" startIcon={<Download />} onClick={downloadHistoricalIncomeTemplate}>
                Income Template
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={downloadHistoricalExpenseTemplate}>
                Expense Template
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Restore color="warning" />
              <Typography variant="h6" fontWeight="bold">Validate and Import Workbook</Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel id="financial-import-mode-label">Import operation</InputLabel>
              <Select
                labelId="financial-import-mode-label"
                value={importMode}
                label="Import operation"
                onChange={(event) => {
                  setImportMode(event.target.value);
                  setSelectedFile(null);
                  setValidationResult(null);
                  setImportResult(null);
                  setErrorMessage('');
                }}
              >
                {IMPORT_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity={['restore_exact', 'replace_matching'].includes(importMode) ? 'warning' : 'info'}>
              {selectedMode?.description}
            </Alert>

            <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
              Select Excel Workbook
              <input hidden type="file" accept=".xlsx" onChange={handleFileSelection} />
            </Button>

            {selectedFile && (
              <Chip label={selectedFile.name} color="primary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                startIcon={isValidating ? <CircularProgress size={18} color="inherit" /> : <FactCheck />}
                onClick={handleValidate}
                disabled={!selectedFile || isValidating || isImporting}
              >
                {isValidating ? 'Validating...' : 'Validate Workbook'}
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={isImporting ? <CircularProgress size={18} color="inherit" /> : <WarningAmber />}
                onClick={handleImport}
                disabled={!validationResult || validationResult.errors.length > 0 || isImporting || isValidating}
              >
                {isImporting ? 'Importing...' : 'Import Validated Records'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {validationResult && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Validation Results</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${validationResult.records.length} records`} color="primary" />
              <Chip label={`${validationResult.errors.length} errors`} color={validationResult.errors.length ? 'error' : 'success'} />
              <Chip label={`${validationResult.warnings.length} warnings`} color={validationResult.warnings.length ? 'warning' : 'default'} />
            </Stack>

            {validationResult.manifest && (
              <Alert severity="success">
                Backup for {validationResult.manifest['Church Name'] || validationResult.manifest['Church ID']},
                exported {validationResult.manifest['Exported At']}.
              </Alert>
            )}

            {validationResult.errors.map((error, index) => (
              <Alert severity="error" key={`error-${index}`}>{error}</Alert>
            ))}
            {validationResult.warnings.map((warning, index) => (
              <Alert severity="warning" key={`warning-${index}`}>{warning}</Alert>
            ))}

            {validationResult.records.length > 0 && (
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Collection</TableCell>
                      <TableCell>Document ID / External ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResult.records.slice(0, 100).map((record, index) => (
                      <TableRow key={`${record.collectionName}-${record.documentId || index}`}>
                        <TableCell>{record.collectionName}</TableCell>
                        <TableCell>{record.documentId || record.data?.externalRecordId || '(new ID)'}</TableCell>
                        <TableCell>{record.data?.status || ''}</TableCell>
                        <TableCell align="right">
                          {typeof record.data?.amount === 'number' ? record.data.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {validationResult.records.length > 100 && (
              <Typography variant="caption" color="text.secondary">
                Preview shows the first 100 records. All {validationResult.records.length} validated records will be imported.
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {importResult && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Import Results</Typography>
            <Divider />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${importResult.created} created`} color="success" />
              <Chip label={`${importResult.updated} updated`} color="primary" />
              <Chip label={`${importResult.skipped} skipped`} />
              <Chip label={`${importResult.failed} failed`} color={importResult.failed ? 'error' : 'default'} />
            </Stack>
            {importResult.errors?.map((error, index) => (
              <Alert severity="error" key={`import-error-${index}`}>{error}</Alert>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default FinancialDataManager;
